import { Button, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { dbManagerService } from "../../../api/dbManager.service.js";
import styles from "./DbManager.module.css";

type LogTailDrawerProps = {
  isOpen: boolean;
  keyspace: string;
  runId: string | null;
  onClose: () => void;
};

export function LogTailDrawer({ isOpen, keyspace, runId, onClose }: LogTailDrawerProps) {
  const [lines, setLines] = useState<string[]>([]);
  const [endState, setEndState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const preRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!isOpen || !runId) {
      setLines([]);
      setEndState(null);
      setError(null);
      return;
    }

    const subscription = dbManagerService.subscribeToSyncLog(keyspace, runId, {
      onLine: line => {
        setLines(prev => [...prev, line]);
      },
      onEnd: state => {
        setEndState(state);
      },
      onError: err => {
        setError(err.message);
      },
    });

    return () => {
      subscription.close();
    };
  }, [isOpen, keyspace, runId]);

  useEffect(() => {
    if (preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [lines]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={runId ? `Sync log — ${runId}` : "Sync log"}
      size="large"
      blurBackground
    >
      <div className={styles.modalBody}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <pre ref={preRef} className={styles.logContent}>
          {lines.length === 0 ? "Waiting for log output..." : lines.join("\n")}
        </pre>

        {endState && (
          <div className={styles.logSummary}>
            <strong>Sync ended:</strong> {endState}
          </div>
        )}

        <div className={styles.actions}>
          <Button variant="contained" color="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
